import React from 'react';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';

/**
 * Get photo URL from Firebase Storage path
 * @param storagePath - The storage path of the photo
 * @returns Promise<string> - The download URL or empty string if path is invalid
 */
export const getPhotoUrl = async (storagePath?: string): Promise<string> => {
  if (!storagePath) {
    return '';
  }
  
  try {
    const storageRef = ref(storage, storagePath);
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    console.error('Error getting photo URL:', error);
    return '';
  }
};

/**
 * Hook to get photo URL with loading state and automatic optimization
 * @param storagePath - The storage path of the photo
 * @param maxWidth - Maximum width for optimization (default: 800)
 * @param quality - Image quality for optimization (default: 80)
 * @returns object with url, loading, and error states
 */
export const usePhotoUrl = (storagePath?: string, maxWidth: number = 800, quality: number = 80) => {
  const [url, setUrl] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!storagePath) {
      setUrl('');
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    getPhotoUrl(storagePath)
      .then((downloadURL) => {
        // Apply optimization to the downloaded URL
        const optimizedUrl = getOptimizedImageUrl(downloadURL, maxWidth, quality);
        setUrl(optimizedUrl);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error loading photo URL:', err);
        setError(err.message || 'Failed to load photo');
        setLoading(false);
      });
  }, [storagePath, maxWidth, quality]);

  return { url, loading, error };
};

/**
 * Optimize Firebase Storage URLs for better performance
 * @param originalUrl - The original Firebase Storage URL
 * @param maxWidth - Maximum width for the optimized image (default: 800)
 * @param quality - Image quality between 0 and 100 (default: 80)
 * @returns string - The optimized URL or original URL if optimization fails
 */
export const getOptimizedImageUrl = (originalUrl: string, maxWidth: number = 800, quality: number = 80): string => {
  if (!originalUrl || !originalUrl.includes('firebasestorage.googleapis.com')) {
    return originalUrl;
  }
  
  try {
    // For Firebase Storage, we can append transformation parameters
    // These work with Firebase's built-in image resizing service
    const url = new URL(originalUrl);
    
    // Add image transformation parameters
    // Note: Firebase Storage supports some basic transformations
    url.searchParams.set('w', maxWidth.toString()); // width
    url.searchParams.set('q', quality.toString()); // quality
    
    return url.toString();
  } catch (error) {
    console.warn('Failed to optimize image URL:', error);
    return originalUrl;
  }
};

/**
 * Compress image file to reduce size before upload
 * @param file - The image file to compress
 * @param maxWidth - Maximum width for the compressed image (default: 800)
 * @param maxHeight - Maximum height for the compressed image (default: 600)
 * @param quality - Compression quality between 0 and 1 (default: 0.8)
 * @returns Promise<File> - The compressed image file
 */
export const compressImage = (
  file: File,
  maxWidth: number = 800,
  maxHeight: number = 600,
  quality: number = 0.8
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress the image
      ctx?.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          } else {
            reject(new Error('Failed to compress image'));
          }
        },
        file.type,
        quality
      );
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for compression'));
    };

    img.src = URL.createObjectURL(file);
  });
};